import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiClock,
  FiTag,
  FiUser,
  FiArrowLeft,
  FiSave
} from "react-icons/fi";
import axios from "axios";
import Swal from "sweetalert2";

const EditShift = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const API_URL = `${process.env.REACT_APP_API_BASE_URL}/v1/shift`;
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  const [userRole, setUserRole] = useState(null);
  const [showAccessDenied, setShowAccessDenied] = useState(false);

  const [shift, setShift] = useState({
    id: "",
    name: "",
    code: "P",
    start_time: "08:00",
    end_time: "17:00",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const role = (localStorage.getItem("role") || "").toLowerCase();
    setUserRole(role);
    if (role && !["owner", "admin", "superadmin", "manager"].includes(role)) {
      setShowAccessDenied(true);
    }
  }, []);

  useEffect(() => {
    const fetchShift = async () => {
      try {
        const response = await axios.get(API_URL, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const resData = response.data;
        const rawList = Array.isArray(resData?.data)
          ? resData.data
          : Array.isArray(resData?.data?.data)
          ? resData.data.data
          : [];

        const foundShift = rawList.find((item) => String(item.id) === String(id));

        if (foundShift) {
          // Extract HH:mm from ISO or string
          const parseTime = (timeStr) => {
            if (!timeStr) return "08:00";
            if (timeStr.includes("T")) {
              const timePart = timeStr.split("T")[1];
              return timePart.substring(0, 5);
            }
            return timeStr.substring(0, 5);
          };

          setShift({
            id: foundShift.id,
            name: foundShift.name || "",
            code: foundShift.code || "P",
            start_time: parseTime(foundShift.start_time),
            end_time: parseTime(foundShift.end_time),
          });
        } else {
          setError("Shift dengan ID tersebut tidak ditemukan!");
        }
      } catch (err) {
        console.error("Error fetch shift detail:", err);
        setError("Gagal mengambil data detail shift.");
      } finally {
        setLoading(false);
      }
    };

    fetchShift();
  }, [id, API_URL, token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setShift((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();

    if (!shift.name.trim() || !shift.code || !shift.start_time || !shift.end_time) {
      Swal.fire({
        title: "Form Belum Lengkap",
        text: "Semua field nama, kode, jam mulai, dan jam pulang wajib diisi!",
        icon: "warning",
        customClass: { popup: "rounded-3xl" },
      });
      return;
    }

    setSaving(true);

    try {
      const formatToISO = (time) => {
        if (!time) return new Date().toISOString();
        const today = new Date().toISOString().split("T")[0];
        const parts = time.split(":");
        const hours = (parts[0] || "00").padStart(2, "0");
        const minutes = (parts[1] || "00").padStart(2, "0");
        return `${today}T${hours}:${minutes}:00Z`;
      };

      const payload = {
        id: shift.id,
        name: shift.name,
        code: shift.code,
        start_time: formatToISO(shift.start_time),
        end_time: formatToISO(shift.end_time),
      };

      const response = await axios.put(`${API_URL}/${id}`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200 || response.status === 201) {
        await Swal.fire({
          title: "Diperbarui!",
          text: `Data shift "${shift.name}" berhasil diubah.`,
          icon: "success",
          timer: 1800,
          showConfirmButton: false,
          customClass: { popup: "rounded-3xl" },
        });

        navigate("/enterprise/shift");
      }
    } catch (err) {
      console.error("Error update shift:", err);
      Swal.fire({
        title: "Gagal Mengubah Shift",
        text: err.response?.data?.message || "Terjadi kesalahan server.",
        icon: "error",
        customClass: { popup: "rounded-3xl" },
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20 min-h-screen">
        <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-md mx-auto my-20 p-6 bg-white rounded-3xl shadow-xl text-center space-y-4">
        <h2 className="text-lg font-bold text-rose-600">Kesalahan Memuat Data</h2>
        <p className="text-xs text-gray-500">{error}</p>
        <button
          onClick={() => navigate("/enterprise/shift")}
          className="px-4 py-2 rounded-xl bg-teal-600 text-white text-xs font-bold"
        >
          Kembali ke Daftar Shift
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-12">
      {/* Header Form */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/enterprise/shift")}
            className="p-2.5 rounded-2xl bg-white hover:bg-gray-100 border border-gray-200 text-gray-700 transition-colors shadow-sm"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
              Edit Shift: {shift.name}
            </h1>
            <p className="text-xs text-gray-500">
              Perbarui jadwal jam masuk, jam pulang, dan kode shift
            </p>
          </div>
        </div>
      </motion.div>

      {/* Main Card Form */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-white rounded-3xl p-6 md:p-8 shadow-xl shadow-gray-200/50 border border-gray-100"
      >
        <form onSubmit={handleSave} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Nama Shift */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiUser className="text-teal-600 w-4 h-4" />
                Nama Shift
              </label>
              <input
                type="text"
                name="name"
                value={shift.name}
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-semibold"
                required
              />
            </div>

            {/* Kode Shift */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiTag className="text-teal-600 w-4 h-4" />
                Kode Shift
              </label>
              <div className="flex flex-wrap gap-2 pt-1">
                {[
                  { code: "P", label: "Pagi (P)", color: "bg-emerald-500 text-white" },
                  { code: "S", label: "Siang (S)", color: "bg-amber-500 text-white" },
                  { code: "L", label: "Libur (L)", color: "bg-rose-500 text-white" },
                  { code: "N", label: "Malam (N)", color: "bg-indigo-500 text-white" },
                ].map((item) => (
                  <button
                    key={item.code}
                    type="button"
                    onClick={() => setShift({ ...shift, code: item.code })}
                    className={`px-4 py-2 rounded-2xl text-xs font-extrabold transition-all shadow-xs ${
                      shift.code === item.code
                        ? `${item.color} shadow-md scale-105`
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Jam Mulai */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiClock className="text-teal-600 w-4 h-4" />
                Jam Masuk (Mulai)
              </label>
              <input
                type="time"
                name="start_time"
                value={shift.start_time}
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
                required
              />
            </div>

            {/* Jam Pulang */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiClock className="text-teal-600 w-4 h-4" />
                Jam Pulang (Selesai)
              </label>
              <input
                type="time"
                name="end_time"
                value={shift.end_time}
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
                required
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={() => navigate("/enterprise/shift")}
              className="px-5 py-2.5 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold transition-colors"
            >
              Batal
            </button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={saving}
              className="px-6 py-2.5 rounded-2xl bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white text-xs font-bold shadow-lg shadow-teal-600/20 transition-all flex items-center gap-2"
            >
              <FiSave className="w-4 h-4" />
              <span>{saving ? "Menyimpan..." : "Simpan Perubahan"}</span>
            </motion.button>
          </div>
        </form>
      </motion.div>

      {/* Access Denied Overlay */}
      {showAccessDenied && (
        <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white p-6 rounded-3xl shadow-2xl w-full max-w-sm text-center space-y-4">
            <h2 className="text-xl font-bold text-gray-900">Akses Ditolak</h2>
            <p className="text-xs text-gray-500">
              Anda tidak memiliki izin untuk mengedit shift.
            </p>
            <button
              onClick={() => navigate("/enterprise/shift")}
              className="w-full py-2.5 rounded-2xl bg-teal-600 text-white text-xs font-bold hover:bg-teal-700"
            >
              Kembali
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditShift;
